import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@byscott-io/corebyscott";
import { api } from "../lib/api";

const CONTEXT_LABELS = {
  all: "All Clients",
  server: "Server (Ruby)",
  mobile: "Mobile (React Native)",
  web: "Web (JavaScript)",
};

function AppCard({ app, onSelect }) {
  return (
    <div
      onClick={() => onSelect(app)}
      className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{app.name}</h3>
        <span className={`px-2 py-1 text-xs rounded-full ${
          app.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
        }`}>
          {app.active ? "Active" : "Inactive"}
        </span>
      </div>
      <div className="text-sm text-gray-500 space-y-1">
        <p>Events: {app.events_count?.toLocaleString() || 0}</p>
        <p>Last used: {app.api_key_last_used_at
          ? new Date(app.api_key_last_used_at).toLocaleString()
          : "Never"}</p>
        <p>Created: {new Date(app.created_at).toLocaleDateString()}</p>
      </div>
    </div>
  );
}

function ApiKeyDisplay({ app, onRegenerate, isRegenerating }) {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(app.api_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">API Key</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowKey(!showKey)}
            className="text-sm text-indigo-600 hover:text-indigo-800"
          >
            {showKey ? "Hide" : "Show"}
          </button>
          <button
            onClick={handleCopy}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <code className="flex-1 text-sm bg-white px-3 py-2 rounded border font-mono">
          {showKey ? app.api_key : app.api_key?.substring(0, 12) + "..." + app.api_key?.slice(-4)}
        </code>
        <button
          onClick={onRegenerate}
          disabled={isRegenerating}
          className="px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
        >
          {isRegenerating ? "Regenerating..." : "Regenerate"}
        </button>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Use this key in the <code className="bg-gray-200 px-1 rounded">X-Metrics-Api-Key</code> header
      </p>
    </div>
  );
}

const LOG_LEVELS = ["debug", "info", "warn", "error", "fatal"];

function SettingInput({ name, value, definition, onChange, disabled }) {
  const { type, default: defaultValue } = definition;

  if (type === "boolean") {
    return (
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={value ?? defaultValue}
          onChange={(e) => onChange(name, e.target.checked)}
          disabled={disabled}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
      </label>
    );
  }

  if (type === "float") {
    return (
      <input
        type="number"
        min="0"
        max="1"
        step="0.1"
        value={value ?? defaultValue}
        onChange={(e) => onChange(name, parseFloat(e.target.value))}
        disabled={disabled}
        className="w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
      />
    );
  }

  if (type === "array") {
    const arrValue = value ?? defaultValue;
    return (
      <input
        type="text"
        value={Array.isArray(arrValue) ? arrValue.join(", ") : arrValue}
        onChange={(e) => onChange(name, e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
        disabled={disabled}
        placeholder="comma-separated values"
        className="w-64 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
      />
    );
  }

  // Special handling for log_min_level - show as dropdown
  if (name === "log_min_level") {
    return (
      <select
        value={value ?? defaultValue}
        onChange={(e) => onChange(name, e.target.value)}
        disabled={disabled}
        className="w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
      >
        {LOG_LEVELS.map((level) => (
          <option key={level} value={level}>
            {level.charAt(0).toUpperCase() + level.slice(1)}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      type="text"
      value={value ?? defaultValue ?? ""}
      onChange={(e) => onChange(name, e.target.value)}
      disabled={disabled}
      className="w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
    />
  );
}

function AppSettings({ appId, isAdmin }) {
  const queryClient = useQueryClient();
  const [localSettings, setLocalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["app-settings", appId],
    queryFn: () => api.getAppSettings(appId),
  });

  const saveMutation = useMutation({
    mutationFn: (settings) => api.updateAppSettings(appId, settings),
    onSuccess: () => {
      queryClient.invalidateQueries(["app-settings", appId]);
      setHasChanges(false);
    },
  });

  useEffect(() => {
    if (data?.settings) {
      setLocalSettings(data.settings);
    }
  }, [data]);

  const handleChange = (name, value) => {
    setLocalSettings(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(localSettings);
  };

  const handleReset = () => {
    if (data?.settings) {
      setLocalSettings(data.settings);
      setHasChanges(false);
    }
  };

  if (isLoading) {
    return <div className="bg-gray-100 h-48 rounded-lg animate-pulse" />;
  }

  const definitions = data?.definitions || {};

  // Group settings by context
  const groupedSettings = {};
  Object.entries(definitions).forEach(([name, def]) => {
    const context = def.context || "all";
    if (!groupedSettings[context]) groupedSettings[context] = [];
    groupedSettings[context].push({ name, ...def });
  });

  return (
    <div className="space-y-6">
      {Object.entries(groupedSettings).map(([context, settings]) => (
        <div key={context} className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-4">
            {CONTEXT_LABELS[context] || context}
          </h4>
          <div className="space-y-4">
            {settings.map(({ name, type, default: defaultValue }) => (
              <div key={name} className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {name.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <p className="text-xs text-gray-500">
                    Default: {JSON.stringify(defaultValue)}
                  </p>
                </div>
                <SettingInput
                  name={name}
                  value={localSettings[name]}
                  definition={{ type, default: defaultValue }}
                  onChange={handleChange}
                  disabled={!isAdmin}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {isAdmin && (
        <div className="flex items-center justify-end gap-4 pt-4 border-t">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Reset
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}

      {saveMutation.isError && (
        <p className="text-red-600 text-sm">{saveMutation.error?.message}</p>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">How settings work:</p>
        <p>
          Settings are returned with every API response from the metrics endpoints.
          Clients cache these settings and apply them automatically.
          Changes take effect on the next API call from each client.
        </p>
      </div>
    </div>
  );
}

export default function Apps() {
  const { currentContainer, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [selectedApp, setSelectedApp] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [showNewAppForm, setShowNewAppForm] = useState(false);
  const [newAppName, setNewAppName] = useState("");

  const { data: apps, isLoading } = useQuery({
    queryKey: ["apps"],
    queryFn: api.getApps,
  });

  // When an app is selected, fetch its full details including API key
  const { data: appDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["app", selectedApp?.id],
    queryFn: () => api.getApp(selectedApp.id),
    enabled: !!selectedApp,
  });

  const createAppMutation = useMutation({
    mutationFn: (name) => api.createApp({ app: { name } }),
    onSuccess: (newApp) => {
      queryClient.invalidateQueries(["apps"]);
      setShowNewAppForm(false);
      setNewAppName("");
      // Select the new app to show its API key
      setSelectedApp(newApp);
    },
  });

  const regenerateKeyMutation = useMutation({
    mutationFn: (appId) => api.regenerateApiKey(appId),
    onSuccess: () => {
      queryClient.invalidateQueries(["app", selectedApp?.id]);
      queryClient.invalidateQueries(["apps"]);
    },
  });

  if (!currentContainer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading account...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Apps</h1>
          <p className="text-gray-500">Manage registered apps and their API keys</p>
        </div>
        {!selectedApp && isAdmin && (
          <button
            onClick={() => setShowNewAppForm(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Create App
          </button>
        )}
      </div>

      {showNewAppForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New App</h3>
          <div className="flex gap-4">
            <input
              type="text"
              value={newAppName}
              onChange={(e) => setNewAppName(e.target.value)}
              placeholder="App name (e.g., ChurchCare, LifeSkillsPath)"
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
            <button
              onClick={() => createAppMutation.mutate(newAppName)}
              disabled={!newAppName || createAppMutation.isPending}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {createAppMutation.isPending ? "Creating..." : "Create"}
            </button>
            <button
              onClick={() => {
                setShowNewAppForm(false);
                setNewAppName("");
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
          </div>
          {createAppMutation.isError && (
            <p className="mt-2 text-red-600 text-sm">{createAppMutation.error?.message}</p>
          )}
        </div>
      )}

      {selectedApp ? (
        /* App Detail View */
        <div className="space-y-6">
          <button
            onClick={() => { setSelectedApp(null); setActiveTab("details"); }}
            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
          >
            ← Back to Apps
          </button>

          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">{selectedApp.name}</h2>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  selectedApp.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                }`}>
                  {selectedApp.active ? "Active" : "Inactive"}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">Slug: {selectedApp.slug}</p>
            </div>

            {/* Tabs */}
            <div className="border-b">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab("details")}
                  className={`px-6 py-3 text-sm font-medium border-b-2 ${
                    activeTab === "details"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveTab("settings")}
                  className={`px-6 py-3 text-sm font-medium border-b-2 ${
                    activeTab === "settings"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Settings
                </button>
              </nav>
            </div>

            <div className="p-6">
              {activeTab === "details" ? (
                <>
                  {isLoadingDetails ? (
                    <div className="bg-gray-100 h-24 rounded-lg animate-pulse" />
                  ) : appDetails?.api_key ? (
                    <ApiKeyDisplay
                      app={appDetails}
                      onRegenerate={() => {
                        if (confirm("Are you sure? This will invalidate the current API key and any apps using it will stop working until updated.")) {
                          regenerateKeyMutation.mutate(selectedApp.id);
                        }
                      }}
                      isRegenerating={regenerateKeyMutation.isPending}
                    />
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                      No API key found
                    </div>
                  )}

                  <div className="mt-6 pt-6 border-t grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Events</p>
                      <p className="text-lg font-semibold">{appDetails?.events_count?.toLocaleString() || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Values</p>
                      <p className="text-lg font-semibold">{appDetails?.values_count?.toLocaleString() || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Metrics</p>
                      <p className="text-lg font-semibold">{appDetails?.metrics_count?.toLocaleString() || 0}</p>
                    </div>
                  </div>

                  {appDetails?.api_key_last_used_at && (
                    <p className="mt-4 text-sm text-gray-500">
                      Last API activity: {new Date(appDetails.api_key_last_used_at).toLocaleString()}
                    </p>
                  )}
                </>
              ) : (
                <AppSettings appId={selectedApp.id} isAdmin={isAdmin} />
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Apps Grid */
        <div>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-200 rounded-lg h-40 animate-pulse" />
              ))}
            </div>
          ) : apps?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {apps.map((app) => (
                <AppCard key={app.id} app={app} onSelect={setSelectedApp} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <div className="text-4xl mb-4">📱</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No apps registered yet</h3>
              <p className="text-gray-500 mb-4">Create an app to start tracking metrics</p>
              {isAdmin && (
                <button
                  onClick={() => setShowNewAppForm(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Create Your First App
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
